import { appConfigStore } from 'Models/AppConfigStore';
import React, { ReactNode } from 'react';
import dayjs from 'dayjs';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import { LottieSpinner } from '../Lottie/LottieSpinner';
import If from '../../If/If';
import { InvoiceRender } from './Invoicerender';
import Pagination from '../../Pagination/Pagination';
import { UserInvoicesDto } from '../../../../Services/Api/_HumanWritten/InvoiceService';
import { store } from 'Models/Store';
import { FormattedMessage } from 'react-intl';
import { stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { Button, Colors, Display } from '../../Button/Button';
import Icon from '../Icon/Icon';
import { CustomTooltip } from '../CustomTooltip/CustomTooltip';

export interface UserInvoiceListCollectionProps {
	invoices: UserInvoicesDto[] | null;
	totalInvoiceCount: number;
	// Server flag: invoiced transactions were hidden from this non-staff customer's list.
	filtered: boolean;
	perPage: number;
	pageNum: number;
	setPageNum: (pageNum: number) => void;
	searchInput: string;
	setSearchInput: (value: string) => void;
	lastUpdated: Date | null;
}

export function UserInvoicesCollection({
	invoices,
	totalInvoiceCount,
	filtered,
	perPage,
	pageNum,
	setPageNum,
	searchInput,
	setSearchInput,
	lastUpdated,
}: UserInvoiceListCollectionProps) {
	const invoiceRenders: ReactNode[] = [];
	// Staff see invoiced rows (server returns them), so detect from the rows for the staff notice.
	// Non-staff have those rows hidden server-side, so rely on the server `filtered` flag instead.
	const isFiltered = invoices?.some(x => x.isInvoiced) && !appConfigStore.invoicesForInvoicedUsers();

	if (invoices !== null) {
		if (!appConfigStore.invoicesForInvoicedUsers()) {
			invoices
				.filter(x => !x.isInvoiced || store.isStaff)
				.forEach(invoice => {
					invoiceRenders.push(<InvoiceRender key={invoice.invoiceId} invoice={invoice} />);
				});
		} else {
			invoices.forEach(invoice => {
				invoiceRenders.push(<InvoiceRender key={invoice.invoiceId} invoice={invoice} />);
			});
		}
	}

	return (
		<div className="basic-table__black">
			<div className="invoices-list__table-headers">
				<div className="invoices-list__header-row">
					<h4>Invoices</h4>
					<form onSubmit={e => e.preventDefault()}>
						<div className="search-box">
							<input
								id="invoice-search-input"
								placeholder="Search"
								className="fh-basic-search"
								autoComplete="off"
								value={searchInput}
								onChange={e => setSearchInput(e.target.value)}
							/>
							<If condition={stringIsEmpty(searchInput)}>
								<CustomTooltip
									id="invoice-search-tool-tip"
									content="Search by Invoice Number, Booking ID, or Voucher Code"
									toolTipPosition="bottom-left"
								/>
							</If>
							<If condition={stringNotEmpty(searchInput)}>
								<Button
									className="clear-search"
									onClick={e => {
										e.preventDefault();
										setSearchInput('');
									}}
									colors={Colors.Black}
									display={Display.Text}
								>
									<Icon name="cross" />
								</Button>
							</If>
						</div>
					</form>
				</div>
				{isFiltered && store.isStaff && (
					<p className="text m-none p-none grey-7 pb-xs">
						<FormattedMessage
							id="invoiced-users__invoiced-filtered-staff__message"
							defaultMessage="Any invoices marked as (Invoiced) were not sent to the user."
						/>
					</p>
				)}
				{filtered && !store.isStaff && (
					<p className="text m-none p-none grey-7 pb-xs">
						<FormattedMessage
							id="invoiced-users__invoiced-filtered__message"
							defaultMessage={
								// eslint-disable-next-line max-len
								'Transactions that have been invoiced will be emailed to you and not appear in this list.'
							}
						/>
					</p>
				)}
				<table>
					<thead>
						<tr>
							<th>Creation date</th>
							<th>
								<FormattedMessage id="tax-invoice__invoice-number" defaultMessage="Invoice number" />
							</th>
							<th>Type</th>
							<th>Total</th>
							<th />
						</tr>
					</thead>
					<tbody>{invoiceRenders}</tbody>
				</table>
				<If condition={invoices === null}>
					<LottieSpinner />
				</If>
				<If condition={invoices !== null && invoiceRenders.length === 0}>
					<p className="no-content">No invoices</p>
				</If>
				<If condition={invoiceRenders.length > 0}>
					<div className="collection-component">
						<div className="invoice-list__footer">
							{lastUpdated && (
								<p className="grey">
									Last updated {formatStandardTime(lastUpdated)},{' '}
									{dayjs(lastUpdated).format('DD MMM, YYYY')}
								</p>
							)}
						</div>
						<section className="collection__pagination new-style">
							<section className="collection__load">
								<Pagination
									perPage={perPage}
									pageNo={pageNum}
									totalRecords={totalInvoiceCount}
									onPageChange={pageNo => {
										setPageNum(pageNo);
									}}
									showGoToPageBox={false}
									showFirstArrow={false}
									showLastArrow={false}
									leftArrowIcon="arrow-left"
									rightArrowIcon="arrow-right"
								/>
							</section>
						</section>
						<p className="grey">{totalInvoiceCount} records</p>
					</div>
				</If>
			</div>
		</div>
	);
}
